'use client';
import React from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DOWNLOAD_HISTORY, TOPIC_DISTRIBUTION } from '@/lib/mockData';

function CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 shadow-card px-3 py-2">
        <p className="text-xs font-semibold text-stone-700">{label}</p>
        <p className="text-sm font-bold text-primary-600 tabular-nums">
          {payload[0].value} downloads
        </p>
      </div>
    );
  }
  return null;
}

function TopicTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 shadow-card px-3 py-2">
        <p className="text-xs font-semibold text-stone-700">{label}</p>
        <p className="text-sm font-bold text-accent-600 tabular-nums">{payload[0].value} lessons</p>
      </div>
    );
  }
  return null;
}

export default function DashboardCharts() {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      {/* Downloads over time */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-card p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-semibold text-stone-800">Downloads Over Time</h3>
            <p className="text-xs text-stone-500 mt-0.5">Your lesson download activity, last 7 months</p>
          </div>
          <span className="text-xs text-stone-400 bg-stone-100 px-2.5 py-1 rounded-full">
            Oct 2025 – Apr 2026
          </span>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={DOWNLOAD_HISTORY} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="downloadGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#C17F3A" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#C17F3A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe0" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="downloads"
              stroke="#C17F3A"
              strokeWidth={2.5}
              fill="url(#downloadGrad)"
              dot={{ fill: '#C17F3A', r: 4, strokeWidth: 0 }}
              activeDot={{ r: 6, fill: '#C17F3A' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Topic distribution */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-card p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-semibold text-stone-800">Lessons by Topic</h3>
            <p className="text-xs text-stone-500 mt-0.5">Total lessons available in the library by topic</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={TOPIC_DISTRIBUTION} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe0" vertical={false} />
            <XAxis dataKey="topic" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <Tooltip content={<TopicTooltip />} />
            <Bar dataKey="count" fill="#2A7F6F" radius={[6, 6, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}